/**
 * Global Styles using styled-components
 * Provides consistent styling across the entire application
 *
 * Features:
 * - CSS custom properties import for design tokens
 * - Comprehensive reset and base styles
 * - Responsive design system with desktop-first approach
 * - Accessibility enhancements and focus management
 * - Print-friendly styles
 * - High contrast and reduced motion support
 */

import { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  /* CSS variables are imported in index.css */

  /* CSS Reset and Normalization */
  * {
    box-sizing: border-box;
  }

  /* Remove default margins and paddings */
  * {
    margin: 0;
    padding: 0;
  }

  /* Better text rendering */
  html {
    width: 100%;
    -webkit-text-size-adjust: 100%;
    -ms-text-size-adjust: 100%;
  }

  body {
    width: 100%;
    position: relative;
    font-family: var(--font-family-base);
    background: var(--surface-strong);
    color: var(--text-primary);
    margin: 0;
    padding: 0;
    min-height: 100vh;
    line-height: var(--line-height-normal);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-feature-settings: 'kern' 1, 'liga' 1, 'calt' 1;
    text-rendering: optimizeLegibility;
  }

  /* Root element improvements */
  #root {
    width: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  /* Screen Reader Only Content */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Enhanced Focus Management */
  *:focus-visible {
    outline: 3px solid var(--brand-600);
    outline-offset: 2px;
    outline-style: solid;
  }

  /* Remove default focus for mouse users */
  *:focus:not(:focus-visible) {
    outline: none;
  }

  /* Enhanced focus for interactive elements */
  button:focus-visible,
  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible,
  [role="button"]:focus-visible,
  [role="tab"]:focus-visible,
  [role="menuitem"]:focus-visible,
  [tabindex]:focus-visible {
    outline: 3px solid var(--brand-600);
    outline-offset: 2px;
    outline-style: solid;
  }

  /* Focus for links */
  a:focus-visible {
    outline: 3px solid var(--brand-600);
    outline-offset: 2px;
    border-radius: 2px;
  }

  /* Skip link for keyboard navigation */
  .skip-link {
    position: absolute;
    top: -40px;
    left: 6px;
    background: var(--action-primary);
    color: white;
    padding: 8px 16px;
    border-radius: 4px;
    text-decoration: none;
    font-weight: 600;
    z-index: 9999;
    transition: top 0.3s ease;
  }

  .skip-link:focus {
    top: 6px;
  }

  /* Enhanced Utility Classes */
  .hidden {
    display: none !important;
  }

  .visually-hidden {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }

  /* Text alignment utilities */
  .text-center { text-align: center; }
  .text-left { text-align: left; }
  .text-right { text-align: right; }
  .text-justify { text-align: justify; }

  /* Display utilities */
  .block { display: block; }
  .inline { display: inline; }
  .inline-block { display: inline-block; }
  .flex { display: flex; }
  .inline-flex { display: inline-flex; }
  .grid { display: grid; }
  .inline-grid { display: inline-grid; }
  .none { display: none; }

  /* Flex utilities */
  .flex-col { flex-direction: column; }
  .flex-row { flex-direction: row; }
  .flex-wrap { flex-wrap: wrap; }
  .flex-nowrap { flex-wrap: nowrap; }
  .items-start { align-items: flex-start; }
  .items-center { align-items: center; }
  .items-end { align-items: flex-end; }
  .justify-start { justify-content: flex-start; }
  .justify-center { justify-content: center; }
  .justify-end { justify-content: flex-end; }
  .justify-between { justify-content: space-between; }
  .justify-around { justify-content: space-around; }

  /* Spacing utilities */
  .space-x-1 > * + * { margin-left: var(--space-xs); }
  .space-x-2 > * + * { margin-left: var(--space-sm); }
  .space-x-4 > * + * { margin-left: var(--space-md); }
  .space-y-1 > * + * { margin-top: var(--space-xs); }
  .space-y-2 > * + * { margin-top: var(--space-sm); }
  .space-y-4 > * + * { margin-top: var(--space-md); }

  /* Position utilities */
  .relative { position: relative; }
  .absolute { position: absolute; }
  .fixed { position: fixed; }
  .sticky { position: sticky; }

  /* Width and height utilities */
  .w-full { width: 100%; }
  .h-full { height: 100%; }
  .w-auto { width: auto; }
  .h-auto { height: auto; }

  /* Overflow utilities */
  .overflow-hidden { overflow: hidden; }
  .overflow-auto { overflow: auto; }
  .overflow-scroll { overflow: scroll; }

  .main-content {
    max-width: 1100px;
    margin: 0 auto;
    padding: 0 var(--space-md);
  }

  /* Auth screens share the same scale on desktop and phones. */
  .auth-title { margin: 0 0 0.5rem; color: var(--text-primary); font-size: 1.5rem; font-weight: 600; line-height: 1.3; letter-spacing: -0.025em; text-align: left; }
  .auth-subtitle { margin: 0 0 1.5rem; color: var(--text-muted); font-size: 0.875rem; line-height: 1.6; text-align: left; }

  .btn-block { 
    width: 100%; 
  }

  .btn-google {
    background: var(--surface);
    color: var(--text-primary);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: var(--space-md) var(--space-lg);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-md);
    font-weight: var(--font-weight-semibold);
    transition: all var(--transition-normal);
    text-decoration: none;
    cursor: pointer;
    font-size: var(--font-size-base);

    &:hover { 
      background: var(--neutral-25);
      box-shadow: var(--shadow-light);
    }

    &:focus {
      outline: 3px solid var(--brand-600);
      outline-offset: 2px;
    }

    svg {
      width: 20px;
      height: 20px;
      flex-shrink: 0;
    }
  }

  .divider {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    gap: var(--space-md);
    color: var(--text-muted);
    font-size: var(--font-size-sm);
    margin: var(--space-lg) 0;

    &::before,
    &::after {
      content: '';
      height: 1px;
      background: var(--border);
      display: block;
    }
  }

  .form-actions {
    display: flex;
    flex-direction: column;
    gap: var(--space-md);
    margin-top: var(--space-sm);
  }

  .helper-row {
    display: flex;
    justify-content: center;
    margin-top: var(--space-lg);
    font-size: var(--font-size-sm);

    a {
      color: var(--brand-600);
      text-decoration: none;
      font-weight: var(--font-weight-medium);
      transition: color var(--transition-normal);

      &:hover {
        color: var(--brand-700);
        text-decoration: underline;
      }

      &:focus {
        outline: 2px solid var(--brand-600);
        outline-offset: 2px;
        border-radius: 2px;
      }
    }
  }

  .forgot-password-link {
    text-align: right;
    margin-bottom: var(--space-md);

    a {
      color: var(--text-muted);
      text-decoration: none;
      font-size: var(--font-size-sm);
      transition: color var(--transition-normal);

      &:hover {
        color: var(--brand-600);
        text-decoration: underline;
      }

      &:focus {
        outline: 2px solid var(--brand-600);
        outline-offset: 2px;
        border-radius: 2px;
      }
    }
  }

  /* Form styling */
  input, select, textarea {
    font-family: inherit;
    font-size: var(--font-size-base);
  }

  @media (max-width: 767px) {
    input, select, textarea { font-size: 1rem; }
  }

  /* Button reset */
  button {
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  /* Link styling */
  a {
    color: var(--brand-600);
    transition: color var(--transition-normal);
  }

  /* Enhanced Responsive Design System */
  /* Desktop-first approach with mobile overrides */

  /* Desktop-first container system */
  .container-lg {
    max-width: 1024px;
    margin: 0 auto;
    padding: 0 var(--space-xl);
  }

  .container-xl {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 var(--space-2xl);
  }

  .container-2xl {
    max-width: 1536px;
    margin: 0 auto;
    padding: 0 var(--space-3xl);
  }

  /* Desktop layout optimizations */
  .main-content {
    max-width: 1200px;
    padding: 0 var(--space-xl);
  }

  /* Mobile overrides - apply when screen is smaller */
  /* Tablet and below (≤1023px) */
  @media (max-width: 1023px) {
    .container-lg {
      max-width: 100%;
      padding: 0 var(--space-lg);
    }

    .container-xl {
      max-width: 100%;
      padding: 0 var(--space-lg);
    }

    .container-2xl {
      max-width: 100%;
      padding: 0 var(--space-md);
    }

    .main-content {
      max-width: 100%;
      padding: 0 var(--space-lg);
    }

    /* Tablet optimizations */

  }

  /* Mobile devices (≤767px) */
  @media (max-width: 767px) {
    .container-lg,
    .container-xl,
    .container-2xl {
      max-width: 100%;
      padding: 0 var(--space-md);
    }

    .main-content {
      padding: 0 var(--space-md);
    }

    /* Enhanced mobile styles */


    /* Mobile touch targets */
    .btn-google {
      min-height: 44px;
      padding: var(--space-md);
      font-size: var(--font-size-sm);
    }

    .form-actions {
      gap: var(--space-sm);
    }

    /* Stack flex items on mobile */
    .flex-mobile-col {
      flex-direction: column;
    }

    /* Reduce spacing on mobile */
    .space-mobile-reduced > * + * {
      margin-top: var(--space-sm);
    }
  }

  /* Small mobile devices (≤479px) */
  @media (max-width: 479px) {
    .container-lg,
    .container-xl,
    .container-2xl {
      max-width: 100%;
      padding: 0 var(--space-sm);
    }

    .main-content {
      padding: 0 var(--space-sm);
    }

    /* Extra small mobile optimizations */

  }

  /* Extra small devices (≤360px) - Ultra compact */
  @media (max-width: 360px) {
    .container-lg,
    .container-xl,
    .container-2xl {
      padding: 0 var(--space-xs); /* Even tighter: 4px */
    }

    .main-content {
      padding: 0 var(--space-xs);
    }


    .btn-google {
      padding: var(--space-sm);
      font-size: var(--font-size-xs);
    }

    /* Force smaller gaps in spacing utilities */
    .space-y-2 > * + * { margin-top: var(--space-xs); }
    .space-y-4 > * + * { margin-top: var(--space-sm); }
  }

  /* Mobile-specific utility classes */
  .hide-on-mobile {
    @media (max-width: 767px) {
      display: none !important;
    }
  }

  .show-on-mobile {
    display: none;
    @media (max-width: 767px) {
      display: block !important;
    }
  }

  .mobile-full-width {
    @media (max-width: 767px) {
      width: 100% !important;
      max-width: 100% !important;
    }
  }

  .mobile-no-padding {
    @media (max-width: 767px) {
      padding: 0 !important;
    }
  }

  .mobile-compact-spacing > * + * {
    @media (max-width: 767px) {
      margin-top: var(--space-sm) !important;
    }
  }

  /* Text overflow prevention utilities */
  .text-wrap {
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
  }

  .text-ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .text-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .text-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* Print styles for better document printing */
  @media print {
    *,
    *::before,
    *::after {
      background: transparent !important;
      color: #000 !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }

    a,
    a:visited {
      text-decoration: underline;
    }

    .no-print {
      display: none !important;
    }

    .print-break-before {
      page-break-before: always;
    }

    .print-break-after {
      page-break-after: always;
    }

    .print-break-inside {
      page-break-inside: avoid;
    }
  }

  /* Enhanced Accessibility Features */

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    *:focus-visible {
      outline-width: 4px;
      outline-color: var(--text-primary);
    }

    /* Ensure sufficient contrast for interactive elements */
    button,
    [role="button"],
    a {
      border: 2px solid transparent;
    }

    button:focus-visible,
    [role="button"]:focus-visible,
    a:focus-visible {
      border-color: var(--text-primary);
    }
  }

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }

    /* Disable smooth scrolling */
    html {
      scroll-behavior: auto;
    }
  }

  /* Color scheme preferences */
  @media (prefers-color-scheme: dark) {
    /* Dark mode styles are defined in variables.css */
    /* Additional dark mode specific styles can be added here if needed */
  }

  /* Enhanced scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--surface-strong);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--text-muted);
  }

  /* Better scrollbar styling for mobile */
  @media (max-width: 767px) {
    ::-webkit-scrollbar {
      height: 4px;
      width: 4px;
    }

    ::-webkit-scrollbar-thumb {
      background: var(--brand-300);
      border-radius: 2px;
    }
  }

  /* Selection styling */
  ::selection {
    background: var(--brand-50);
    color: var(--brand-700);
  }

  ::-moz-selection {
    background: var(--brand-50);
    color: var(--brand-700);
  }
`;
